import { useEffect, useRef, useState } from "react";

// Shared Extended-Warranty list scroll cap — used by the PDP
// (components/Product/Detail.js) and the cart + mini-cart
// (components/ExtendedWarranty/EWSlots.js).
//
// BRD Cart Page Case 2 (BA-confirmed 2026-06-04, also applies to the mini-cart
// drawer and — per EA-305 — the PDP): on desktop show at most VISIBLE_EW_SLOTS
// EW slot blocks, then scroll the list internally. Mobile (and ≤ N slots) shows
// all. It measures the real rendered height of the first VISIBLE_EW_SLOTS blocks
// (+ row gaps) so the cap lands exactly on a slot boundary.
//
// Args:
//   count              — number of slot blocks currently rendered (drives re-measure)
//   { enabled = true } — set false to disable the cap (e.g. EWSlots readonly mode)
//
// Returns { scrollRef, maxHeight }:
//   attach scrollRef to the list container; when maxHeight != null apply
//   `overflow-y-auto pe-2` and style={{ maxHeight: `${maxHeight}px` }}.
export const VISIBLE_EW_SLOTS = 3;
const DESKTOP_MQ = "(min-width: 768px)"; // Tailwind `md`

export default function useEwSlotsScrollCap(count, { enabled = true } = {}) {
  const scrollRef = useRef(null);
  const [maxHeight, setMaxHeight] = useState(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !enabled) {
      setMaxHeight(null);
      return;
    }
    const mq = window.matchMedia(DESKTOP_MQ);
    const measure = () => {
      const blocks = Array.from(el.children);
      if (!mq.matches || blocks.length <= VISIBLE_EW_SLOTS) {
        setMaxHeight(null); // mobile, or ≤ N slots → no cap, show all
        return;
      }
      const cs = window.getComputedStyle(el);
      const gap = parseFloat(cs.rowGap || cs.gap) || 0;
      let h = 0;
      for (let i = 0; i < VISIBLE_EW_SLOTS; i++) {
        h += blocks[i].getBoundingClientRect().height;
        if (i < VISIBLE_EW_SLOTS - 1) h += gap;
      }
      setMaxHeight(Math.round(h));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    Array.from(el.children).forEach((c) => ro.observe(c));
    mq.addEventListener?.("change", measure);
    return () => {
      ro.disconnect();
      mq.removeEventListener?.("change", measure);
    };
  }, [count, enabled]);

  return { scrollRef, maxHeight };
}
